/**
 * SCD R1 — Tunnel d'ouverture de magasin : types, localisations, devis.
 * Utilisé par le wizard (premier magasin) et la page Magasin & réseau
 * (ouvertures suivantes). Le tunnel est rejoué à CHAQUE ouverture.
 */
import typesData from '../data/dgs/types_magasin.json';
import locsData from '../data/dgs/localisations.json';
import { getSecteur, getPositionnement, prixConseille, demandeBaseJour } from './retail';
import { getVille } from './villes';

export const TYPES_MAGASIN = typesData.types;
/** Formats proposés à l'ouverture (GSS et corner désactivés — « à venir »). */
export const TYPES_MAGASIN_ACTIFS = typesData.types.filter(t => t.actif !== false);
export const LOCALISATIONS = locsData.localisations;
export const LOYER_BASE_M2 = locsData.loyer_base_m2;

export const getTypeMagasin = (id) => TYPES_MAGASIN.find(t => t.id === id);
export const getLocalisation = (id) => LOCALISATIONS.find(l => l.id === id);

/* Coefficient de coût d'agencement selon le positionnement prix. */
const COEF_AGENCEMENT_POS = { entree: 0.85, coeur: 1.0, premium: 1.35 };

/** Un type est-il compatible avec une localisation ? */
export const compatibles = (typeId, locId) => {
  const loc = getLocalisation(locId);
  const type = getTypeMagasin(typeId);
  if (!loc || !type) return { ok: false, raison: 'inconnu' };
  if ((loc.interdits || []).includes(typeId)) return { ok: false, raison: loc.interdit_raison };
  if (loc.surface_max && type.surface_min > loc.surface_max) {
    return { ok: false, raison: `Surface minimale du format (${type.surface_min} m²) supérieure au maximum de la localisation (${loc.surface_max} m²).` };
  }
  return { ok: true };
};

/** Surface effective : défaut du type, plafonnée par la localisation. */
export const surfaceEffective = (typeId, locId) => {
  const type = getTypeMagasin(typeId);
  const loc = getLocalisation(locId);
  if (!type) return 0;
  let s = type.surface_defaut;
  if (loc?.surface_max) s = Math.min(s, loc.surface_max);
  return Math.max(type.surface_min, s);
};

/**
 * Devis complet de l'étape 4 : coût d'ouverture, loyer, trafic estimé,
 * CA potentiel/jour et point mort estimé (jours).
 */
export const computeDevis = ({ typeId, positionnement = 'coeur', localisationId, secteurId, villeId }) => {
  const type = getTypeMagasin(typeId);
  const loc = getLocalisation(localisationId);
  if (!type || !loc) return null;
  const ville = getVille(villeId);
  const vBassin = ville?.bassin || 1;
  const vLoyer = ville?.coef_loyer || 1;
  const vCout = ville?.coef_cout || 1;
  const surface = surfaceEffective(typeId, localisationId);
  const coutAgencement = Math.round(surface * type.cout_agencement_m2 * (COEF_AGENCEMENT_POS[positionnement] || 1));
  const loyerMensuel = Math.round(surface * LOYER_BASE_M2 * loc.coef_loyer * vLoyer);
  const chargesMensuelles = Math.round(loyerMensuel * 0.22);
  const depotGarantie = loyerMensuel * 3;
  const coutOuverture = Math.round((type.travaux_fixes + coutAgencement) * vCout) + depotGarantie;

  // Trafic & CA potentiel/jour à partir de la demande de base du secteur.
  const sec = getSecteur(secteurId);
  let demandeJour = 0;
  let panierMoyen = 0;
  if (sec) {
    let caJour = 0;
    sec.familles.forEach(f => {
      const d = demandeBaseJour(f, secteurId, positionnement, 'independant', 6);
      demandeJour += d;
      caJour += d * prixConseille(f, positionnement);
    });
    demandeJour = Math.round(demandeJour * loc.coef_trafic * type.trafic_base_mult * vBassin);
    panierMoyen = caJour > 0 ? (caJour / Math.max(1, demandeJour / (sec.articles_par_achat || 2))) : 0;
    panierMoyen *= loc.coef_panier;
  }
  const acheteursJour = Math.round(demandeJour / ((sec?.articles_par_achat) || 2));
  const caPotentielJour = Math.round(acheteursJour * panierMoyen * (loc.concurrence_malus || 1));
  const margePct = 0.42;
  // Point mort HORS masse salariale (l'équipe est gérée et payée par ailleurs).
  const chargesJour = (loyerMensuel + chargesMensuelles) / 30;
  const margeJour = caPotentielJour * margePct;
  const pointMortJours = margeJour > chargesJour
    ? Math.ceil(coutOuverture / (margeJour - chargesJour))
    : null;

  return {
    type, loc, ville: ville || null, surface, coutAgencement, loyerMensuel, chargesMensuelles,
    depotGarantie, coutOuverture, traficEstime: acheteursJour,
    caPotentielJour, pointMortJours,
  };
};

/** Construit l'objet magasin stocké dans le state à partir du tunnel. */
export const buildMagasinFromTunnel = ({ nom, typeId, positionnement, localisationId, villeId, secteurId = null, ouvertJour = 0 }) => {
  const type = getTypeMagasin(typeId);
  const loc = getLocalisation(localisationId);
  const ville = getVille(villeId);
  const surface = surfaceEffective(typeId, localisationId);
  const loyerMensuel = Math.round(surface * LOYER_BASE_M2 * loc.coef_loyer * (ville?.coef_loyer || 1));
  return {
    id: `mag-${Date.now().toString(36)}`,
    nom: nom || `${type.nom} — ${ville?.nom || loc.nom}`,
    typeId,
    localisationId,
    villeId: villeId || null,
    secteurId: secteurId || null,
    positionnement,
    surface,
    loyerMensuel,
    chargesMensuelles: Math.round(loyerMensuel * 0.22),
    coefTrafic: loc.coef_trafic * type.trafic_base_mult * (ville?.bassin || 1),
    coefPanier: loc.coef_panier,
    concurrenceMalus: loc.concurrence_malus || 1,
    ouvertJour,
    capaciteReserve: type.capacite_reserve,
  };
};
